import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Chip, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, Snackbar, Alert } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PhoneIcon from "@mui/icons-material/Phone";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { decryptId } from "../helper/idCodec.js";
import { API_ENDPOINTS, getData, postData } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";
import Form from "react-bootstrap/Form";
import "./Trips.css";

// ---------------- Reusable Components ----------------
const InfoBox = ({ value, label, bg, color }) => (
  <Box flex="0 0 48%" textAlign="center" sx={{ bgcolor: bg, p: 2, mb: 2 }}>
    <Typography variant="h6" fontWeight="bold" color={color}>
      {value}
    </Typography>
    <Typography variant="body2">{label}</Typography>
  </Box>
);

const PaymentBox = ({ title, data }) => {
  const amount = Number(data.amount) || 0;
  return (
    <Paper variant="outlined" sx={{ flex: 1, p: 2, bgcolor: "#f5f5f5" }}>
      <Typography variant="body2" fontWeight={600}>
        {title}
      </Typography>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Typography variant="body2">Orders:</Typography>
        <Typography variant="body2">{data.packages}</Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Typography variant="body2">Total Amount:</Typography>
        <Typography variant="body2">₹{amount.toFixed(2)}</Typography>
      </Box>
    </Paper>
  );
};

const InfoRowBox = ({ label, value, color }) => {
  const numericValue = Number(value) || 0;
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        p: 1.5,
        border: "1px solid #ddd",
        borderRadius: "8px",
        bgcolor: "#f9f9f9",
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} color={color || "inherit"}>
        ₹{numericValue.toFixed(2)}
      </Typography>
    </Box>
  );
};

// ---------------- Main Component ----------------
const TripDetails = () => {
  const { id } = useParams();
  const tripId = id ? decryptId(id) : "";
  const navigate = useNavigate();

  const [trip, setTrip] = useState(null);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [packageInfo, setPackageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("wallet");
  const [paymentAmount, setPaymentAmount] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [resultDialog, setResultDialog] = useState({ open: false, success: null });

  const handleClosePaymentDialog = () => setOpenPaymentDialog(false);

  const fetchTrip = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getData(API_ENDPOINTS.fetchTripById, { tripId });
      setTrip(data.result.trip);
      setPaymentsSummary(data.result.payments_summary);
      setPackageInfo(data.result.package_info);
    } catch (e) {
      setError("Unable to fetch trip details.");
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (tripId) fetchTrip();
  }, [tripId, fetchTrip]);

  const handleConfirmPayment = async () => {
    if (!trip) return;

    if (!paymentAmount || Number(paymentAmount) <= 0) {
      setSnackbarMessage("Please enter a valid payout amount.");
      setSnackbarOpen(true);
      return;
    }

    if (!paymentMethod) {
      setSnackbarMessage("Please select a payment method.");
      setSnackbarOpen(true);
      return;
    }

    try {
      const finalAmount = Number(paymentsSummary?.finalAmount) || 0;
      const payload = {
        trip_id: trip.id,
        wallet_id: trip.owner_wallet_id,
        total_amount: finalAmount,
        payout_amount: Number(paymentAmount),
        payment_method: paymentMethod,
      };

      const response = await postData(API_ENDPOINTS.createOwnerPayment, payload);

      setResultDialog({ open: true, success: response?.statusCode === 200 });
      handleClosePaymentDialog();
    } catch (error) {
      setResultDialog({ open: true, success: false });
      handleClosePaymentDialog();
    }
  };

  if (loading) return <LoadingSpinner isLoading={true} />;
  if (error)
    return (
      <Typography p={3} color="error">
        {error}
      </Typography>
    );
  if (!trip) return <Typography p={3}>No trip data found.</Typography>;

  const route = trip.TripStops?.map((stop) => stop.city_name).join(" – ");
  const owner = trip.Truck?.Owner
    ? `${trip.Truck.Owner.first_name || ""} ${trip.Truck.Owner.last_name || ""}`.trim()
    : "N/A";
  const driver = trip.Drivers
    ? `${trip.Drivers.first_name || ""} ${trip.Drivers.last_name || ""}`.trim()
    : "N/A";
  const driverPhone = trip.Drivers?.mobile_num || "N/A";

  const totalBalance =
    (paymentsSummary?.cod.amount || 0) +
    (paymentsSummary?.wallet.amount || 0) +
    (paymentsSummary?.online.amount || 0);
  const codOutstanding = paymentsSummary?.cod.amount || 0;
  const platformFee = paymentsSummary?.platform.amount || 0;
  const finalAmount = Number(paymentsSummary?.finalAmount) || 0;
  const outstandingAmount = Number(paymentsSummary?.outstanding) || 0;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <img
            src="/smart-vectors/Back.svg"
            alt="back"
            style={{ width: 32, height: 32, cursor: "pointer" }}
            onClick={() => navigate(`/app/trips`)}
          />
          <Box>
            <Typography variant="h5" fontWeight={600}>Trip Details</Typography>
            <Typography variant="body2" color="text.secondary">Trip ID: {trip.id}</Typography>
          </Box>
        </Box>
        <Chip label={trip.trip_completed ? "Completed" : "Active"} color={trip.trip_completed ? "primary" : "success"} />
      </Box>

      {/* Route Info */}
      <Paper
        elevation={1}
        sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <LocalShippingIcon color="primary" />
          <Box>
            <Typography variant="h6">{route || "Route not available"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {trip.trip_started ? "Route Active" : "Not started"} | Total Distance: {trip.trip_in_kms} km
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Info Panels */}
      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2} mt={2}>
        {/* Owner Info */}
        <Paper sx={{ flex: 1, p: 2, borderRadius: "16px" }}>
          <Typography variant="subtitle1" fontWeight={600}>Owner Info</Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body2">Truck Owner</Typography>
          <Typography variant="body1" fontWeight={600}>{owner}</Typography>
          <Typography variant="body2" mt={2}>Mobile Number</Typography>
          <Box display="flex" alignItems="center" mt={1}>
            <PhoneIcon sx={{ fontSize: 18, mr: 1 }} color="action" />
            <Typography variant="body2" fontWeight={600}>{driverPhone}</Typography>
          </Box>
          <Typography variant="body2" mt={2}>Truck Name</Typography>
          <Typography variant="body1" fontWeight={600}>{trip.Truck?.truck_name || "N/A"}</Typography>
          <Typography variant="body2" mt={2}>Truck Number</Typography>
          <Typography variant="body1" fontWeight={600}>{trip.Truck?.truck_no || "N/A"}</Typography>
        </Paper>

        {/* Driver Info */}
        <Paper sx={{ flex: 1, p: 2, borderRadius: "16px" }}>
          <Typography variant="subtitle1" fontWeight={600}>Driver Info</Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body2">Driver Name</Typography>
          <Typography variant="body1" fontWeight={600}>{driver}</Typography>
          <Typography variant="body2" mt={2}>Mobile Number</Typography>
          <Box display="flex" alignItems="center" mt={1}>
            <PhoneIcon sx={{ fontSize: 18, mr: 1 }} color="action" />
            <Typography variant="body2" fontWeight={600}>{driverPhone}</Typography>
          </Box>
          <Typography variant="body2" mt={2}>License Number</Typography>
          <Typography variant="body1" fontWeight={600}>{trip.Drivers?.license_no || "N/A"}</Typography>
        </Paper>

        {/* Package Info */}
        {packageInfo && (
          <Paper sx={{ flex: 1, p: 2, borderRadius: "16px" }}>
            <Typography variant="subtitle1" fontWeight={600}>Package Info</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" mt={2}>
              <InfoBox value={packageInfo.total_packages} label="Total" bg="#FFEBEE" />
              <InfoBox value={packageInfo.delivered_packages} label="Delivered" bg="#E8F5E9" color="success.main" />
              <InfoBox value={packageInfo.ongoing_packages} label="Ongoing" bg="#E3F2FD" color="primary.main" />
              <InfoBox value={packageInfo.accepted_packages} label="Accepted" bg="#FFFDE7" color="warning.main" />
            </Box>
          </Paper>
        )}
      </Box>

      {/* Payment Info */}
      {paymentsSummary && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>Payment Info</Typography>
            <Typography variant="subtitle1" color="primary">Total Amount Received ₹{totalBalance.toFixed(2)}</Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
            <PaymentBox title="Cash on Delivery (COD)" data={paymentsSummary.cod} />
            <PaymentBox title="Online Payment" data={paymentsSummary.online} />
            <PaymentBox title="Wallet Payment" data={paymentsSummary.wallet} />
            <PaymentBox title="Platform Revenue" data={paymentsSummary.platform} />
          </Box>
          <Box display="flex" justifyContent="space-between" alignContent="center" mt={3}>
            <Typography variant="subtitle1" color="orange">Outstanding: ₹{outstandingAmount.toFixed(2)}</Typography>
            {outstandingAmount > 0 && (
              <Button variant="contained" color="primary" startIcon={<AttachMoneyIcon />} onClick={() => setOpenPaymentDialog(true)}>
                Initiate Payment
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Initiate Payment</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <InfoRowBox label="Total Amount Received" value={totalBalance} />
            <InfoRowBox label="COD Amount" value={codOutstanding} color="red" />
            <InfoRowBox label="Platform Fee" value={platformFee} color="red" />
            <InfoRowBox label="Total" value={finalAmount} color="grey" />
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Payment Amount</Typography>
            <Form.Control type="number" placeholder="Enter Amount" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Payment Method</Typography>
              <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <FormControlLabel value="wallet" control={<Radio />} label="Digital Wallet" />
              </RadioGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePaymentDialog} variant="outlined" sx={{ borderRadius: "8px", textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" sx={{ backgroundColor: "#6d9dfc", borderRadius: "8px", textTransform: "none" }} onClick={handleConfirmPayment}>Confirm Payment</Button>
        </DialogActions>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={resultDialog.open} onClose={() => setResultDialog({ open: false, success: null })}>
        <DialogContent sx={{ textAlign: "center", p: 6 }}>
          {resultDialog.success ? (
            <>
              <img src="/smart-vectors/Payment Successful.svg" alt="success" style={{ width: 80, marginBottom: 16 }} />
              <Typography variant="h6" color="success.main">Payment Successful!</Typography>
            </>
          ) : (
            <>
              <img src="/smart-vectors/Payment Failed.svg" alt="failure" style={{ width: 80, marginBottom: 16 }} />
              <Typography variant="h6" color="error">Payment Failed</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button variant="contained" color={resultDialog.success ? "success" : "error"} onClick={async () => { setResultDialog({ open: false, success: null }); await fetchTrip(); }}>
            OK
          </Button>
          {!resultDialog.success && <Button variant="outlined" onClick={() => setResultDialog({ open: false, success: null })}>Cancel</Button>}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: "100%" }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TripDetails;
